import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Mic,
  Square,
  Copy,
  Download,
  Loader2,
  LogOut,
  Languages,
  Volume2,
  Menu,
  CheckCircle,
  Sparkles,
  X,
  Send,
  Minus,
  CreditCard,
} from "lucide-react";
import VoiceVisualizer from "@/components/VoiceVisualizer";
import HistorySidebar from "@/components/HistorySidebar";
import VoiceLogo from "@/components/VoiceLogo";
import ShortcutsModal from "@/components/ShortcutsModal";
import SubscribeModal from "@/components/SubscribeModal";
import CancelSubscriptionModal from "@/components/CancelSubscriptionModal";
import EmojiModal from "@/components/EmojiModal";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Support Modal Component
function SupportModal({ isOpen, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "You must provide a name";
    if (!email.trim()) newErrors.email = "You must provide an email address";
    if (!message.trim()) newErrors.message = "You must provide a message";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/support`, { name: name.trim(), email: email.trim(), message: message.trim() }, { withCredentials: true });
      // Message sent - close modal (no toast)
      setName("");
      setEmail("");
      setMessage("");
      setErrors({});
      onClose();
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }} onClick={onClose} />
      <div className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md rounded-2xl p-6" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: '#f5f5f5' }}>Contact Support</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1" style={{ color: '#a0aec0' }}><X className="h-5 w-5" /></Button>
        </div>
        <div className="space-y-3">
          <div>
            <input type="text" value={name} onChange={(e) => { setName(e.target.value); setErrors({...errors, name: ''}); }} placeholder="Your Name" className="w-full px-4 py-2.5 rounded-lg text-sm" style={{ backgroundColor: '#0f1419', color: '#f5f5f5', border: errors.name ? '1px solid #ef4444' : '1px solid #2d3748' }} />
            {errors.name && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{errors.name}</p>}
          </div>
          <div>
            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErrors({...errors, email: ''}); }} placeholder="Your Email" className="w-full px-4 py-2.5 rounded-lg text-sm" style={{ backgroundColor: '#0f1419', color: '#f5f5f5', border: errors.email ? '1px solid #ef4444' : '1px solid #2d3748' }} />
            {errors.email && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{errors.email}</p>}
          </div>
          <div>
            <textarea 
              value={message} 
              onChange={(e) => { 
                if (e.target.value.length <= 5000) {
                  setMessage(e.target.value); 
                  setErrors({...errors, message: ''});
                }
              }} 
              placeholder="How can we help?" 
              rows={4} 
              className="w-full px-4 py-2.5 rounded-lg text-sm resize-none" 
              style={{ backgroundColor: '#0f1419', color: '#f5f5f5', border: errors.message ? '1px solid #ef4444' : '1px solid #2d3748' }} 
              maxLength={5000}
            />
            <div className="flex justify-between mt-1">
              {errors.message && <p className="text-xs" style={{ color: '#ef4444' }}>{errors.message}</p>}
              <p className="text-xs ml-auto" style={{ color: '#718096' }}>{message.length}/5000</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={sending} className="w-full rounded-lg py-2.5" style={{ backgroundColor: '#6366f1', color: '#fff' }}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send Message
          </Button>
        </div>
      </div>
    </>
  );
}

export default function DictationPage({ user, onLogout }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [originalTranscription, setOriginalTranscription] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [history, setHistory] = useState([]);
  const [shortcuts, setShortcuts] = useState([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("es");
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPreparingSpeech, setIsPreparingSpeech] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState("English");
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [showToneOptions, setShowToneOptions] = useState(false);
  const [showLanguageOptions, setShowLanguageOptions] = useState(false);
  const [isCheckingErrors, setIsCheckingErrors] = useState(false);
  const [isTransformingTone, setIsTransformingTone] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [currentTone, setCurrentTone] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [showEmojiModal, setShowEmojiModal] = useState(false);
  
  // Hotkey mode flag - when true, auto-fix/copy after transcription
  // This is ONLY set when recording is started via hotkey (Ctrl+Win) or Electron global hotkey
  const hotkeyModeRef = useRef(false);
  
  // Track if app is minimized (for auto-clear behavior)
  // Auto-clear only happens when minimized + hotkey mode
  const isMinimizedRef = useRef(false);
  
  // Voice command listener state
  const [voiceCommandsEnabled, setVoiceCommandsEnabled] = useState(false); // Start disabled, user must click to enable
  const [voiceCommandsListening, setVoiceCommandsListening] = useState(false);
  const [voiceCommandError, setVoiceCommandError] = useState(null);
  const voiceCommandRecognitionRef = useRef(null);
  const stopCommandRecognitionRef = useRef(null); // Separate recognition for stop command during recording
  // Refs to store recording functions for voice commands
  const startRecordingRef = useRef(null);
  const stopRecordingRef = useRef(null);
  
  // PWA Install state
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  
  // Subscription state
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [blockReason, setBlockReason] = useState(null);

  const requireText = (action, actionName) => {
    if (!transcription || !transcription.trim()) {
      // No text - silently return without action
      return false;
    }
    action();
    return true;
  };

  const languages = [
    { code: "af", name: "Afrikaans", flag: "🇿🇦" },
    { code: "ar", name: "Arabic", flag: "🇸🇦" },
    { code: "bn", name: "Bengali", flag: "🇧🇩" },
    { code: "en-GB", name: "British English", flag: "🇬🇧" },
    { code: "bg", name: "Bulgarian", flag: "🇧🇬" },
    { code: "zh", name: "Chinese", flag: "🇨🇳" },
    { code: "hr", name: "Croatian", flag: "🇭🇷" },
    { code: "cs", name: "Czech", flag: "🇨🇿" },
    { code: "da", name: "Danish", flag: "🇩🇰" },
    { code: "nl", name: "Dutch", flag: "🇳🇱" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "et", name: "Estonian", flag: "🇪🇪" },
    { code: "tl", name: "Filipino", flag: "🇵🇭" },
    { code: "fi", name: "Finnish", flag: "🇫🇮" },
    { code: "fr", name: "French", flag: "🇫🇷" },
    { code: "de", name: "German", flag: "🇩🇪" },
    { code: "el", name: "Greek", flag: "🇬🇷" },
    { code: "he", name: "Hebrew", flag: "🇮🇱" },
    { code: "hi", name: "Hindi", flag: "🇮🇳" },
    { code: "hu", name: "Hungarian", flag: "🇭🇺" },
    { code: "id", name: "Indonesian", flag: "🇮🇩" },
    { code: "it", name: "Italian", flag: "🇮🇹" },
    { code: "ja", name: "Japanese", flag: "🇯🇵" },
    { code: "ko", name: "Korean", flag: "🇰🇷" },
    { code: "lv", name: "Latvian", flag: "🇱🇻" },
    { code: "lt", name: "Lithuanian", flag: "🇱🇹" },
    { code: "ms", name: "Malay", flag: "🇲🇾" },
    { code: "no", name: "Norwegian", flag: "🇳🇴" },
    { code: "pl", name: "Polish", flag: "🇵🇱" },
    { code: "pt", name: "Portuguese", flag: "🇵🇹" },
    { code: "ro", name: "Romanian", flag: "🇷🇴" },
    { code: "ru", name: "Russian", flag: "🇷🇺" },
    { code: "sr", name: "Serbian", flag: "🇷🇸" },
    { code: "sk", name: "Slovak", flag: "🇸🇰" },
    { code: "sl", name: "Slovenian", flag: "🇸🇮" },
    { code: "es", name: "Spanish", flag: "🇪🇸" },
    { code: "sw", name: "Swahili", flag: "🇰🇪" },
    { code: "sv", name: "Swedish", flag: "🇸🇪" },
    { code: "th", name: "Thai", flag: "🇹🇭" },
    { code: "tr", name: "Turkish", flag: "🇹🇷" },
    { code: "uk", name: "Ukrainian", flag: "🇺🇦" },
    { code: "vi", name: "Vietnamese", flag: "🇻🇳" },
  ];

  const toneOptions = [
    { id: "original", name: "Original", icon: "↩️" },
    { id: "website_build", name: "Website Build", icon: "🏗️" },
    { id: "seo_webpage", name: "SEO-Webpage", icon: "🚀" },
    { id: "ai_prompt", name: "AI Prompt", icon: "🤖" },
    { id: "conversational", name: "Conversational", icon: "💬" },
    { id: "personal", name: "Personal", icon: "👤" },
    { id: "engaging", name: "Engaging", icon: "✨" },
    { id: "direct", name: "Direct", icon: "➡️" },
    { id: "professional", name: "Professional", icon: "💼" },
    { id: "technical", name: "Technical", icon: "⚙️" },
    { id: "concise", name: "Concise", icon: "📌" },
    { id: "empathetic", name: "Empathetic", icon: "💝" },
    { id: "assertive", name: "Assertive", icon: "💪" },
    { id: "educational", name: "Educational", icon: "📚" },
  ];

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const textareaRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    fetchShortcuts();
    fetchSubscriptionStatus();
  }, []);

  // Track window visibility (minimized/hidden state)
  useEffect(() => {
    const handleVisibilityChange = () => {
      isMinimizedRef.current = document.hidden;
      console.log('Window visibility changed:', document.hidden ? 'hidden/minimized' : 'visible');
    };
    
    // Also listen for Electron minimize events
    const handleElectronMinimize = () => {
      isMinimizedRef.current = true;
      console.log('Electron: Window minimized');
    };
    
    const handleElectronRestore = () => {
      isMinimizedRef.current = false;
      console.log('Electron: Window restored');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('electron-window-minimized', handleElectronMinimize);
    window.addEventListener('electron-window-restored', handleElectronRestore);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('electron-window-minimized', handleElectronMinimize);
      window.removeEventListener('electron-window-restored', handleElectronRestore);
    };
  }, []);

  // Voice Command Recognition - Listens for "Voice Capture Transcribe/End Transcription/Paste"
  useEffect(() => {
    // Check if Web Speech API is available
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.log('Voice commands not available - Web Speech API not supported');
      setVoiceCommandError('Voice commands not supported in this browser. Use Chrome.');
      return;
    }
    
    if (!voiceCommandsEnabled) {
      if (voiceCommandRecognitionRef.current) {
        try {
          voiceCommandRecognitionRef.current.stop();
        } catch (e) {}
        voiceCommandRecognitionRef.current = null;
      }
      setVoiceCommandsListening(false);
      return;
    }
    
    // Don't run voice command listener while actively recording (to avoid conflicts)
    if (isRecording) {
      if (voiceCommandRecognitionRef.current) {
        try {
          voiceCommandRecognitionRef.current.stop();
        } catch (e) {}
      }
      setVoiceCommandsListening(false);
      return;
    }
    
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    let lastTranscript = '';
    let commandCooldown = false;
    
    recognition.onstart = () => {
      console.log('Voice command recognition started');
      setVoiceCommandsListening(true);
      setVoiceCommandError(null);
    };
    
    recognition.onresult = async (event) => {
      // Get the latest transcript
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      
      const normalizedTranscript = transcript.toLowerCase().trim();
      
      // Prevent duplicate command triggers
      if (normalizedTranscript === lastTranscript || commandCooldown) return;
      lastTranscript = normalizedTranscript;
      
      console.log('Voice command heard:', normalizedTranscript);
      
      // Check for voice commands - "Start Transcription" to start
      if (normalizedTranscript.includes('start transcription') ||
          normalizedTranscript.includes('start recording')) {
        if (!isRecording && !isProcessing && startRecordingRef.current) {
          commandCooldown = true;
          console.log('Voice command: Starting transcription');
          toast.success("🎤 Starting recording...", { duration: 2000 });
          
          // Stop the command listener before starting recording
          try { recognition.stop(); } catch(e) {}
          setVoiceCommandsListening(false);
          
          // Small delay to let speech recognition stop
          setTimeout(() => {
            hotkeyModeRef.current = true; // Enable auto-fix/copy
            if (startRecordingRef.current) startRecordingRef.current();
            commandCooldown = false;
          }, 300);
        }
      }
      // "Paste Transcription" to paste
      else if (normalizedTranscript.includes('paste transcription')) {
        if (transcription && !isRecording && !isProcessing) {
          commandCooldown = true;
          console.log('Voice command: Paste');
          
          // Copy to clipboard
          await copyToClipboardSafe(transcription);
          
          // Try to simulate paste using multiple methods
          let pasteSimulated = false;
          
          // Method 1: Electron API (desktop app)
          if (window.electronAPI?.simulatePaste) {
            window.electronAPI.simulatePaste();
            pasteSimulated = true;
          }
          
          // Method 2: Try to trigger Ctrl+V keyboard event (works in some contexts)
          if (!pasteSimulated) {
            try {
              // Create and dispatch a keyboard event
              const pasteEvent = new KeyboardEvent('keydown', {
                key: 'v',
                code: 'KeyV',
                ctrlKey: true,
                bubbles: true,
                cancelable: true
              });
              document.dispatchEvent(pasteEvent);
              
              // Also try execCommand as fallback
              document.execCommand('paste');
            } catch (e) {
              console.log('Paste simulation not available in browser:', e);
            }
          }
          
          toast.success("📋 Copied & Ready! Press Ctrl+V", { duration: 2500 });
          
          // Clear the window after paste command
          setTimeout(() => {
            setTranscription("");
            setOriginalTranscription("");
            setCurrentTone(null);
            commandCooldown = false;
          }, 500);
        }
      }
    };
    
    recognition.onerror = (event) => {
      console.log('Voice command recognition error:', event.error);
      setVoiceCommandsListening(false);
      
      if (event.error === 'not-allowed') {
        setVoiceCommandError('Microphone access denied. Please allow microphone permission.');
        setVoiceCommandsEnabled(false);
        toast.error("Microphone permission denied for voice commands", { duration: 3000 });
      } else if (event.error === 'no-speech') {
        // This is normal - just restart
      } else if (event.error !== 'aborted') {
        setVoiceCommandError(`Error: ${event.error}`);
      }
    };
    
    recognition.onend = () => {
      console.log('Voice command recognition ended, checking if should restart...');
      setVoiceCommandsListening(false);
      // Always try to restart if enabled (the useEffect will handle stopping if recording)
      if (voiceCommandsEnabled) {
        setTimeout(() => {
          // Re-check conditions before restarting
          if (voiceCommandsEnabled && voiceCommandRecognitionRef.current === recognition) {
            try {
              console.log('Restarting voice command recognition...');
              recognition.start();
            } catch (e) {
              console.log('Could not restart voice commands:', e);
            }
          }
        }, 1000); // Longer delay to ensure clean restart
      }
    };
    
    voiceCommandRecognitionRef.current = recognition;
    
    // Start listening
    try {
      recognition.start();
      console.log('Voice commands starting...');
    } catch (e) {
      console.log('Could not start voice command recognition:', e);
      setVoiceCommandError('Could not start voice commands. Try clicking the button again.');
      setVoiceCommandsEnabled(false);
    }
    
    return () => {
      console.log('Cleaning up voice command recognition...');
      if (voiceCommandRecognitionRef.current) {
        try {
          voiceCommandRecognitionRef.current.stop();
        } catch (e) {
          // Ignore stop errors
        }
        voiceCommandRecognitionRef.current = null;
      }
      setVoiceCommandsListening(false);
    };
  }, [voiceCommandsEnabled, isRecording, isProcessing]);

  // Stop Command Listener - runs DURING recording to detect "end transcription" command
  useEffect(() => {
    // Only run when recording AND voice commands are enabled
    if (!isRecording || !voiceCommandsEnabled) {
      if (stopCommandRecognitionRef.current) {
        try {
          stopCommandRecognitionRef.current.stop();
        } catch (e) {}
        stopCommandRecognitionRef.current = null;
      }
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    let commandDetected = false;
    
    recognition.onresult = (event) => {
      if (commandDetected) return;
      
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      
      const lowerTranscript = transcript.toLowerCase();
      console.log('Stop command listener heard:', lowerTranscript);
      
      // Check for stop commands - simplified to just "end transcription"
      const stopCommands = [
        'end transcription',
        'stop transcription',
        'stop recording',
        'end recording'
      ];
      
      for (const cmd of stopCommands) {
        if (lowerTranscript.includes(cmd)) {
          commandDetected = true;
          console.log('STOP COMMAND DETECTED:', cmd);
          toast.success("✋ Stopping recording...", { duration: 2000 });
          
          // Stop this recognition
          try {
            recognition.stop();
          } catch (e) {}
          
          // Stop the actual recording
          if (stopRecordingRef.current) {
            stopRecordingRef.current();
          }
          break;
        }
      }
    };
    
    recognition.onerror = (event) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.log('Stop command recognition error:', event.error);
      }
    };
    
    recognition.onend = () => {
      // Restart if still recording and not command detected
      if (isRecording && voiceCommandsEnabled && !commandDetected) {
        try {
          setTimeout(() => {
            if (stopCommandRecognitionRef.current && isRecording && !commandDetected) {
              try {
                recognition.start();
              } catch (e) {}
            }
          }, 100);
        } catch (e) {}
      }
    };
    
    stopCommandRecognitionRef.current = recognition;
    
    // Start listening for stop command
    try {
      recognition.start();
      console.log('Stop command listener started (listening for "end transcription")');
    } catch (e) {
      console.log('Could not start stop command listener:', e);
    }
    
    return () => {
      if (stopCommandRecognitionRef.current) {
        try {
          stopCommandRecognitionRef.current.stop();
        } catch (e) {}
        stopCommandRecognitionRef.current = null;
      }
    };
  }, [isRecording, voiceCommandsEnabled]);

  // PWA Install Prompt Handler
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setInstallPrompt(e);
      // Show our custom install banner
      setShowInstallBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallBanner(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Listen for paste events (Ctrl+V) to clear the message window
  // This allows users to paste elsewhere, then come back with a clean slate
  const lastCopiedTextRef = useRef("");
  const textWasCopiedRef = useRef(false);
  
  useEffect(() => {
    // Method 1: Listen for paste event on document
    const handlePaste = (e) => {
      if (transcription || textWasCopiedRef.current) {
        console.log('Paste event detected - clearing');
        setTranscription("");
        setOriginalTranscription("");
        setCurrentTone(null);
        textWasCopiedRef.current = false;
        lastCopiedTextRef.current = "";
      }
    };
    
    // Method 2: Listen for Ctrl+V keydown
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V' || e.keyCode === 86)) {
        if (transcription || textWasCopiedRef.current) {
          console.log('Ctrl+V keydown detected - clearing');
          // Clear immediately
          setTranscription("");
          setOriginalTranscription("");
          setCurrentTone(null);
          textWasCopiedRef.current = false;
          lastCopiedTextRef.current = "";
        }
      }
    };
    
    // Method 3: When user returns to the page after copying, clear the text
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && textWasCopiedRef.current) {
        console.log('Page visible again after copy - clearing');
        setTranscription("");
        setOriginalTranscription("");
        setCurrentTone(null);
        textWasCopiedRef.current = false;
        lastCopiedTextRef.current = "";
      }
    };
    
    // Method 4: When window regains focus after copying, clear
    const handleFocus = () => {
      if (textWasCopiedRef.current) {
        console.log('Window focused after copy - clearing');
        setTimeout(() => {
          setTranscription("");
          setOriginalTranscription("");
          setCurrentTone(null);
          textWasCopiedRef.current = false;
          lastCopiedTextRef.current = "";
        }, 100);
      }
    };
    
    document.addEventListener('paste', handlePaste, true);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('paste', handlePaste, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [transcription]);

  const handleInstallClick = async () => {
    if (!installPrompt) {
      toast.error("Installation not available. Try using Chrome or Edge.");
      return;
    }
    
    // Show the install prompt
    installPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success("Voice Capture installed! Find it in your apps.");
      setShowInstallBanner(false);
    }
    
    // Clear the saved prompt
    setInstallPrompt(null);
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  }, [transcription]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscriptionStatus(response.data);
      
      // Check if user should be blocked
      if (!response.data.can_use_app) {
        setBlockReason(response.data.block_reason);
        setShowSubscribeModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch subscription status:", error);
    }
  };

  const checkSubscriptionBeforeAction = async (actionType = "transcribe") => {
    // Refresh subscription status before actions
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscriptionStatus(response.data);
      
      if (!response.data.can_use_app) {
        setBlockReason(response.data.block_reason);
        setShowSubscribeModal(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Subscription check failed:", error);
      return true; // Allow action if check fails
    }
  };

  const handleSubscriptionError = (error) => {
    if (error.response?.status === 402) {
      // Payment required - show subscribe modal
      const detail = error.response.data?.detail || "";
      if (detail.includes("trial ended")) {
        setBlockReason("trial_ended");
      } else if (detail.includes("limit reached")) {
        setBlockReason("limit_reached");
      }
      setShowSubscribeModal(true);
      return true;
    }
    return false;
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/history`, { withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  };

  const fetchShortcuts = async () => {
    try {
      const response = await axios.get(`${API}/shortcuts`, { withCredentials: true });
      setShortcuts(response.data);
    } catch (error) {
      console.error("Failed to fetch shortcuts:", error);
    }
  };

  const applyShortcuts = (text) => {
    if (!shortcuts.length || !text) return text;
    let result = text;
    shortcuts.forEach((shortcut) => {
      // Match "shortcut [name]" with flexible spacing and case-insensitive
      const escapedName = shortcut.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regexWithTrigger = new RegExp(`\\bshortcut\\s+${escapedName}\\b`, 'gi');
      result = result.replace(regexWithTrigger, shortcut.content);
    });
    return result;
  };

  // Apply emoji shortcuts - "emoticon [word]" → emoji
  const applyEmojiShortcuts = async (text) => {
    if (!text) return text;
    try {
      const response = await axios.get(`${API}/emoji-shortcuts`, { withCredentials: true });
      const emojiShortcuts = response.data.shortcuts || [];
      
      let result = text;
      emojiShortcuts.forEach((shortcut) => {
        // Match "emoticon [word]" with flexible spacing and case-insensitive
        // Also match common speech-to-text variations like "emoticon", "emotion", "emote icon"
        const escapedWord = shortcut.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Match: emoticon word, emotion icon word, emote icon word, emoji word
        const patterns = [
          `\\bemoticon\\s+${escapedWord}\\b`,
          `\\bemotion\\s*icon\\s+${escapedWord}\\b`,
          `\\bemote\\s*icon\\s+${escapedWord}\\b`,
          `\\bemoji\\s+${escapedWord}\\b`
        ];
        patterns.forEach(pattern => {
          const regex = new RegExp(pattern, 'gi');
          result = result.replace(regex, shortcut.emoji);
        });
      });
      return result;
    } catch (error) {
      console.error("Failed to apply emoji shortcuts:", error);
      return text;
    }
  };

  // Apply create types - "create email [content]" → formatted email
  const applyCreateTypes = async (text) => {
    if (!text) return text;
    try {
      const response = await axios.get(`${API}/create-types`, { withCredentials: true });
      const createTypes = response.data.types || [];
      
      let result = text;
      let matched = false;
      
      createTypes.forEach((type) => {
        // Match the trigger phrase at the start of text
        const escapedTrigger = type.trigger.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`^${escapedTrigger}[,.]?\\s*(.*)`, 'i');
        const match = result.match(regex);
        
        if (match && !matched) {
          matched = true;
          const content = match[1].trim();
          
          // Apply the format template
          let formatted = type.format;
          // Replace [body], [content], [message], [item], [details], etc. with actual content
          formatted = formatted.replace(/\[body\]|\[content\]|\[message\]|\[casual message\]|\[details\]/gi, content);
          
          // For lists, split by commas or "and" and create bullet points
          if (type.id === "todo" || type.name.toLowerCase().includes("list")) {
            const items = content.split(/,\s*|\s+and\s+/i).filter(i => i.trim());
            if (items.length > 0) {
              formatted = items.map(item => `• ${item.trim()}`).join('\n');
            }
          }
          
          result = formatted;
          toast.success(`Formatted as ${type.name}`, { duration: 2000 });
        }
      });
      
      return result;
    } catch (error) {
      console.error("Failed to apply create types:", error);
      return text;
    }
  };

  // Safe clipboard copy that doesn't require permission prompt
  const copyToClipboardSafe = async (text) => {
    try {
      // Try the modern Clipboard API first (no permission needed for writeText)
      await navigator.clipboard.writeText(text);
      // Track that we copied text (for Ctrl+V clear feature)
      lastCopiedTextRef.current = text;
      textWasCopiedRef.current = true;
      console.log('Text copied to clipboard, clear flag set');
    } catch (err) {
      // Fallback to execCommand (older method, no permission needed)
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        // Track that we copied text
        lastCopiedTextRef.current = text;
        textWasCopiedRef.current = true;
        console.log('Text copied to clipboard (fallback), clear flag set');
      } catch (fallbackErr) {
        console.error('Clipboard copy failed:', fallbackErr);
      }
    }
  };

  const convertPunctuation = (text) => {
    if (!text) return text;
    return text
      .replace(/\bperiod\b/gi, '.')
      .replace(/\bcomma\b/gi, ',')
      .replace(/\bquestion mark\b/gi, '?')
      .replace(/\bexclamation point\b/gi, '!')
      .replace(/\bexclamation mark\b/gi, '!')
      .replace(/\bcolon\b/gi, ':')
      .replace(/\bsemicolon\b/gi, ';')
      .replace(/\bnew line\b/gi, '\n')
      .replace(/\bnew paragraph\b/gi, '\n\n')
      .replace(/\s+\./g, '.')
      .replace(/\s+,/g, ',')
      .replace(/\s+\?/g, '?')
      .replace(/\s+!/g, '!')
      .replace(/\s+:/g, ':')
      .replace(/\s+;/g, ';');
  };

  const autoSaveToHistory = async (text) => {
    if (!text || text.trim().length < 10) return;
    try {
      await axios.post(`${API}/history`, { text }, { withCredentials: true });
      await fetchHistory();
    } catch (error) {
      console.error("Auto-save failed:", error);
    }
  };

  // ============================================
  // CROSS-PLATFORM AUDIO RECORDING & CONVERSION
  // Supports: Chrome, Safari (iOS/macOS), Firefox, Android
  // ============================================

  // Detect the best supported MIME type for this browser/device
  const getSupportedMimeType = () => {
    // Priority order: formats that work best across platforms
    const mimeTypes = [
      'audio/webm;codecs=opus',  // Chrome, Firefox, Safari 18.4+
      'audio/webm',              // Chrome, Firefox
      'audio/mp4',               // Safari (iOS/macOS)
      'audio/aac',               // Safari fallback
      'audio/ogg;codecs=opus',   // Firefox
      ''                         // Let browser decide (iOS Safari works best this way)
    ];

    for (const mimeType of mimeTypes) {
      if (mimeType === '' || MediaRecorder.isTypeSupported(mimeType)) {
        console.log(`Using audio format: ${mimeType || 'browser default'}`);
        return mimeType;
      }
    }
    return ''; // Fallback to browser default
  };

  // Get file extension based on MIME type
  const getFileExtension = (mimeType) => {
    if (!mimeType) return 'webm'; // Default
    if (mimeType.includes('mp4') || mimeType.includes('aac') || mimeType.includes('m4a')) return 'mp4';
    if (mimeType.includes('ogg')) return 'ogg';
    return 'webm';
  };

  // Convert audio blob to WAV format using Web Audio API
  // This ensures maximum compatibility with OpenAI's API
  const convertToWav = async (audioBlob) => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        throw new Error('Web Audio API not supported');
      }

      const audioContext = new AudioContextClass();
      const arrayBuffer = await audioBlob.arrayBuffer();
      
      // Decode the audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Target: 16kHz mono (optimal for speech recognition)
      const targetSampleRate = 16000;
      const numChannels = 1;
      const bitsPerSample = 16;
      
      // Calculate duration and create offline context for resampling
      const duration = audioBuffer.duration;
      const offlineContext = new OfflineAudioContext(
        numChannels,
        Math.ceil(duration * targetSampleRate),
        targetSampleRate
      );
      
      // Create buffer source and connect
      const source = offlineContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(offlineContext.destination);
      source.start(0);
      
      // Render the resampled audio
      const resampledBuffer = await offlineContext.startRendering();
      const samples = resampledBuffer.getChannelData(0);
      
      // Create WAV file buffer
      const dataLength = samples.length * 2; // 16-bit = 2 bytes per sample
      const buffer = new ArrayBuffer(44 + dataLength);
      const view = new DataView(buffer);
      
      // Helper to write strings to DataView
      const writeString = (offset, str) => {
        for (let i = 0; i < str.length; i++) {
          view.setUint8(offset + i, str.charCodeAt(i));
        }
      };
      
      // WAV Header
      writeString(0, 'RIFF');                                    // ChunkID
      view.setUint32(4, 36 + dataLength, true);                  // ChunkSize
      writeString(8, 'WAVE');                                    // Format
      writeString(12, 'fmt ');                                   // Subchunk1ID
      view.setUint32(16, 16, true);                              // Subchunk1Size (16 for PCM)
      view.setUint16(20, 1, true);                               // AudioFormat (1 = PCM)
      view.setUint16(22, numChannels, true);                     // NumChannels
      view.setUint32(24, targetSampleRate, true);                // SampleRate
      view.setUint32(28, targetSampleRate * numChannels * 2, true); // ByteRate
      view.setUint16(32, numChannels * 2, true);                 // BlockAlign
      view.setUint16(34, bitsPerSample, true);                   // BitsPerSample
      writeString(36, 'data');                                   // Subchunk2ID
      view.setUint32(40, dataLength, true);                      // Subchunk2Size
      
      // Write audio samples as 16-bit PCM
      let offset = 44;
      for (let i = 0; i < samples.length; i++) {
        // Clamp sample to [-1, 1] and convert to 16-bit integer
        const sample = Math.max(-1, Math.min(1, samples[i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
      
      // Close audio context to free resources
      await audioContext.close();
      
      console.log(`Converted to WAV: ${(buffer.byteLength / 1024).toFixed(1)}KB, ${duration.toFixed(1)}s`);
      return new Blob([buffer], { type: 'audio/wav' });
      
    } catch (error) {
      console.error('WAV conversion error:', error);
      throw error;
    }
  };

  const processAudioChunk = async (audioBlob, originalMimeType) => {
    try {
      setIsProcessing(true);
      
      let audioToSend = audioBlob;
      let filename = `recording.${getFileExtension(originalMimeType)}`;
      
      // Try to convert to WAV for maximum compatibility
      try {
        console.log(`Converting ${originalMimeType || 'audio'} to WAV...`);
        audioToSend = await convertToWav(audioBlob);
        filename = "recording.wav";
        console.log('WAV conversion successful');
      } catch (conversionError) {
        console.warn("WAV conversion failed, sending original format:", conversionError.message);
        // Continue with original format - OpenAI supports webm, mp4, etc.
      }
      
      const formData = new FormData();
      formData.append("audio", audioToSend, filename);
      formData.append("language", "en");

      console.log(`Sending audio: ${filename}, size: ${(audioToSend.size / 1024).toFixed(1)}KB`);

      const response = await axios.post(`${API}/transcribe`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
        timeout: 60000 // 60 second timeout for large files
      });

      let newText = response.data.text;
      const detectedLang = response.data.detected_language || "English";
      
      if (newText && newText.trim() && newText.trim().length > 2) {
        // Filter out common Whisper hallucinations that appear when audio contains silence
        // These are artifacts the model produces when there's no actual speech
        const whisperHallucinations = [
          "thanks for watching",
          "thank you for watching", 
          "thanks for listening",
          "thank you for listening",
          "thank you",
          "thanks",
          "goodbye",
          "bye",
          "the end",
          "see you",
          "see you next time",
          "please subscribe",
          "like and subscribe",
          "subtitles by",
          "translated by",
          "music",
          "applause", 
          "silence",
          "you"
        ];
        
        // Clean the text for comparison (remove punctuation, lowercase)
        const cleanText = newText.trim().toLowerCase().replace(/[^\w\s]/g, '').trim();
        
        if (whisperHallucinations.includes(cleanText)) {
          console.log('Filtered Whisper hallucination:', newText);
          toast.dismiss("transcribing-toast");
          return;
        }
        
        // Skip if text is too short and generic (single words)
        const words = cleanText.split(/\s+/).filter(w => w.length > 0);
        if (words.length <= 1) {
          console.log('Filtered short text:', newText);
          toast.dismiss("transcribing-toast");
          return;
        }
        
        newText = applyShortcuts(newText);
        newText = await applyEmojiShortcuts(newText);  // Apply emoji shortcuts
        newText = await applyCreateTypes(newText);  // Apply create types formatting
        newText = convertPunctuation(newText);
        
        // Check for voice commands in the transcribed text
        const lowerText = newText.toLowerCase();
        let voiceCommandDetected = null;
        
        // Check for "voice capture end transcription" or "voice capture end" at the end
        const endCommands = [
          'voice capture end transcription',
          'voice capture end',
          'voice capture stop',
          'end transcription',
          'stop transcription'
        ];
        
        for (const cmd of endCommands) {
          if (lowerText.includes(cmd)) {
            voiceCommandDetected = 'end';
            // Remove the command from the text
            const regex = new RegExp(cmd.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            newText = newText.replace(regex, '').trim();
            // Clean up any trailing punctuation from command removal
            newText = newText.replace(/[,.\s]+$/, '').trim();
            console.log('Voice command detected in transcription: END');
            break;
          }
        }
        
        // Check for "voice capture paste" 
        const pasteCommands = ['voice capture paste', 'paste it', 'paste this'];
        for (const cmd of pasteCommands) {
          if (lowerText.includes(cmd)) {
            voiceCommandDetected = 'paste';
            // Remove the command from the text
            const regex = new RegExp(cmd.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            newText = newText.replace(regex, '').trim();
            newText = newText.replace(/[,.\s]+$/, '').trim();
            console.log('Voice command detected in transcription: PASTE');
            break;
          }
        }
        
        // If text is empty after removing command, use original without command
        if (!newText.trim()) {
          newText = ""; // Just the command was spoken
        }
        
        const fullText = transcription ? `${transcription} ${newText}`.trim() : newText;
        setTranscription(fullText);
        setOriginalTranscription(fullText);
        setDetectedLanguage(detectedLang);  // Use detected language from API
        setCurrentTone(null);
        
        // Auto-fix errors and auto-copy for ALL modes (both manual click and hotkey)
        console.log("Starting auto-fix for text:", fullText.substring(0, 50) + "...");
        try {
          const fixResponse = await axios.post(`${API}/check-errors`, { text: fullText }, { withCredentials: true });
          console.log("Auto-fix response:", fixResponse.data);
          toast.dismiss("transcribing-toast"); // Dismiss the transcribing toast
          
          let finalText = fullText;
          
          if (fixResponse.data.has_corrections) {
            let fixedText = fixResponse.data.corrected_text;
            // Re-apply emoji shortcuts in case auto-fix modified them
            fixedText = await applyEmojiShortcuts(fixedText);
            setTranscription(fixedText);
            finalText = fixedText;
            console.log("Text was auto-fixed");
          }
          
          // ALWAYS auto-copy to clipboard after transcription + auto-fix (for ALL modes)
          await copyToClipboardSafe(finalText);
          
          // Show appropriate toast based on whether corrections were made and voice command
          if (voiceCommandDetected === 'paste') {
            toast.success("📋 Voice command: Copied & ready to paste!", { duration: 2500 });
            // If in Electron, trigger actual paste
            if (window.electronAPI?.simulatePaste) {
              window.electronAPI.simulatePaste();
            }
            // Clear after paste command
            setTimeout(() => {
              setTranscription("");
              setOriginalTranscription("");
              setCurrentTone(null);
            }, 500);
          } else if (voiceCommandDetected === 'end') {
            toast.success("📋 Copied! (Voice command: End)", { duration: 2000 });
          } else if (fixResponse.data.has_corrections) {
            toast.success("📋 Copied! Errors fixed", { duration: 2000 });
          } else {
            toast.success("📋 Copied!", { duration: 2000 });
          }
          
          // Auto-clear ONLY when minimized (hotkey mode background usage) and no paste command
          if (hotkeyModeRef.current && isMinimizedRef.current && voiceCommandDetected !== 'paste') {
            setTimeout(() => {
              setTranscription("");
              setOriginalTranscription("");
              setCurrentTone(null);
            }, 500);
          }
          
          // Notify Electron if running in desktop app
          if (window.electronAPI?.notifyTranscriptionComplete) {
            window.electronAPI.notifyTranscriptionComplete(finalText);
          }
        } catch (fixError) {
          // If auto-fix fails, still copy the original text
          console.error("Auto-fix failed:", fixError);
          toast.dismiss("transcribing-toast");
          
          // Still auto-copy even if fix failed
          await copyToClipboardSafe(fullText);
          
          // Handle voice command even when auto-fix fails
          if (voiceCommandDetected === 'paste') {
            toast.success("📋 Voice command: Copied & ready to paste!", { duration: 2500 });
            if (window.electronAPI?.simulatePaste) {
              window.electronAPI.simulatePaste();
            }
            setTimeout(() => {
              setTranscription("");
              setOriginalTranscription("");
              setCurrentTone(null);
            }, 500);
          } else {
            toast.success("📋 Copied! (auto-fix unavailable)", { duration: 2000 });
          }
          
          // Auto-clear ONLY when minimized
          if (hotkeyModeRef.current && isMinimizedRef.current && voiceCommandDetected !== 'paste') {
            setTimeout(() => {
              setTranscription("");
              setOriginalTranscription("");
              setCurrentTone(null);
            }, 500);
          }
          
          if (window.electronAPI?.notifyTranscriptionComplete) {
            window.electronAPI.notifyTranscriptionComplete(fullText);
          }
        }
        
        // Reset hotkey mode after processing
        hotkeyModeRef.current = false;
        
        await autoSaveToHistory(fullText);
      } else {
        // Empty or very short response - likely silence, don't show anything
        console.log('Empty or near-empty transcription, ignoring');
        toast.dismiss("transcribing-toast");
      }
    } catch (error) {
      console.error("Transcription error:", error);
      if (!handleSubscriptionError(error)) {
        const errorMessage = error.response?.data?.detail || error.message || "Transcription failed";
        toast.error(errorMessage);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 16000 }  // Optimal for speech
        } 
      });
      streamRef.current = stream;
      
      // Get the best supported MIME type for this browser
      const mimeType = getSupportedMimeType();
      
      // Create MediaRecorder with or without explicit MIME type
      // (iOS Safari works better without specifying MIME type)
      const recorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      
      // Store the actual MIME type being used
      const actualMimeType = mediaRecorder.mimeType || mimeType;
      console.log(`MediaRecorder initialized with: ${actualMimeType}`);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { 
            type: actualMimeType || 'audio/webm' 
          });
          audioChunksRef.current = [];
          console.log(`Recording complete: ${(audioBlob.size / 1024).toFixed(1)}KB, type: ${audioBlob.type}`);
          await processAudioChunk(audioBlob, actualMimeType);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      // Removed toast - recording indicator in UI is enough
    } catch (error) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = useCallback(async () => {
    if (mediaRecorderRef.current && isRecording) {
      setIsProcessing(true);
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    }
  }, [isRecording]);

  // Store recording functions in refs for voice commands to access
  useEffect(() => {
    startRecordingRef.current = startRecording;
  }, []);
  
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  // Listen for Electron hotkey events (Ctrl+Win from global hotkey - works when minimized)
  useEffect(() => {
    const handleElectronStartRecording = () => {
      console.log('Electron Ctrl+Win: Start recording (hotkey mode)');
      if (!isRecording && !isProcessing) {
        hotkeyModeRef.current = true;  // Enable hotkey mode for auto-fix/copy/clear
        toast.dismiss();
        toast("🎤 Recording...", { duration: 30000, id: "recording-toast" });
        startRecording();
      }
    };

    const handleElectronStopRecording = () => {
      console.log('Electron Ctrl+Win: Stop recording');
      if (isRecording) {
        toast.dismiss("recording-toast");
        toast.loading("✨ Transcribing...", { id: "transcribing-toast" });
        stopRecording();
      }
    };

    window.addEventListener('electron-start-recording', handleElectronStartRecording);
    window.addEventListener('electron-stop-recording', handleElectronStopRecording);
    
    return () => {
      window.removeEventListener('electron-start-recording', handleElectronStartRecording);
      window.removeEventListener('electron-stop-recording', handleElectronStopRecording);
    };
  }, [isRecording, isProcessing, startRecording, stopRecording]);

  // Browser keyboard shortcuts (Ctrl+Win) - only works when tab is focused
  // Track if Ctrl+Win is being held
  const ctrlWinHeldRef = useRef(false);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Win = Hold to record (Meta key is the Windows key)
      if (e.ctrlKey && (e.key === 'Meta' || e.key === 'OS')) {
        e.preventDefault();
        if (!ctrlWinHeldRef.current && !isProcessing && !isRecording) {
          ctrlWinHeldRef.current = true;
          hotkeyModeRef.current = true;  // Enable hotkey mode for auto-fix/copy/clear
          toast.dismiss();
          toast("🎤 Recording...", { duration: 30000, id: "recording-toast" });
          startRecording();
        }
      }
    };

    const handleKeyUp = (e) => {
      // Release Ctrl or Win = Stop recording
      if (e.key === 'Meta' || e.key === 'OS' || e.key === 'Control') {
        if (ctrlWinHeldRef.current && isRecording) {
          ctrlWinHeldRef.current = false;
          toast.dismiss("recording-toast");
          toast.loading("✨ Transcribing...", { id: "transcribing-toast" });
          stopRecording();
          
          // Notify Electron that Ctrl+Win was released
          if (window.electronAPI?.send) {
            window.electronAPI.send('ctrl-win-released');
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isRecording, isProcessing, startRecording, stopRecording]);

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      // Manual click - NOT hotkey mode, so no auto-fix/copy/clear
      hotkeyModeRef.current = false;
      startRecording();
    }
  };

  const copyToClipboard = async () => {
    if (!transcription) return;
    try {
      await copyToClipboardSafe(transcription);
      // Show "Copied!" feedback briefly
      setShowCopied(true);
      toast.success("📋 Copied! Ready to paste (Ctrl+V)", { duration: 2000 });
      
      // Auto-clear screen after copying when app is VISIBLE (Copy button clicked)
      setTimeout(() => {
        setShowCopied(false);
        // Clear the screen after copy
        setTranscription("");
        setOriginalTranscription("");
        setCurrentTone(null);
      }, 500);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const downloadTranscription = (format = 'txt') => {
    if (!transcription) {
      return; // Silently return if nothing to download
    }
    
    let content, mimeType, extension;
    switch (format) {
      case 'csv':
        content = `"Timestamp","Language","Text"\n"${new Date().toISOString()}","${detectedLanguage}","${transcription.replace(/"/g, '""')}"`;
        mimeType = 'text/csv';
        extension = 'csv';
        break;
      case 'pdf':
        // For PDF, we'll create a simple text file since true PDF needs libraries
        content = `Voice Capture Transcription\n${'='.repeat(30)}\nDate: ${new Date().toLocaleString()}\nLanguage: ${detectedLanguage}\n\n${transcription}`;
        mimeType = 'text/plain';
        extension = 'txt';
        // PDF saved as formatted text - no toast
        break;
      default:
        content = transcription;
        mimeType = 'text/plain';
        extension = 'txt';
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transcription-${new Date().toISOString().slice(0, 10)}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowDownloadOptions(false);
    // Removed toast - download action is obvious
  };

  const clearTranscription = () => {
    setTranscription("");
    setOriginalTranscription("");
    setCurrentTone(null);
    // Removed toast - clear action is obvious
  };

  const loadFromHistory = (item) => {
    setTranscription(item.text);
    setOriginalTranscription(item.text);
    setCurrentTone(null);
    setShowHistory(false);
    // Removed toast - text appearing is obvious
  };

  const deleteFromHistory = async (id) => {
    try {
      await axios.delete(`${API}/history/${id}`, { withCredentials: true });
      await fetchHistory();
      // Deleted - no toast
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const translateText = async () => {
    if (!transcription) return;
    try {
      setIsTranslating(true);
      const response = await axios.post(`${API}/translate`, {
        text: transcription,
        target_language: selectedLanguage
      }, { withCredentials: true });
      setTranscription(response.data.translated_text);
      // Removed toast - text change is obvious
    } catch (error) {
      if (!handleSubscriptionError(error)) {
        toast.error("Translation failed");
      }
    } finally {
      setIsTranslating(false);
    }
  };

  const readAloud = async () => {
    if (!transcription) return;
    try {
      setIsPreparingSpeech(true);
      const response = await axios.post(`${API}/speak`, {
        text: transcription,
        voice: "nova",
        speed: 1.0
      }, { 
        withCredentials: true
      });
      
      setIsPreparingSpeech(false);
      setIsSpeaking(true);
      
      // Convert base64 to audio blob
      const audioBase64 = response.data.audio_base64;
      const byteCharacters = atob(audioBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const audioBlob = new Blob([byteArray], { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => {
          setIsSpeaking(false);
          URL.revokeObjectURL(audioUrl);
        };
        audioRef.current.play();
      }
    } catch (error) {
      setIsPreparingSpeech(false);
      setIsSpeaking(false);
      toast.error("Read aloud failed");
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
  };

  const checkErrors = async () => {
    if (!transcription) return;
    try {
      setIsCheckingErrors(true);
      const response = await axios.post(`${API}/check-errors`, {
        text: transcription
      }, { withCredentials: true });
      
      if (response.data.has_corrections) {
        setTranscription(response.data.corrected_text);
        // Corrections applied - visible in text
      }
      // No toast - text changes are obvious
    } catch (error) {
      if (!handleSubscriptionError(error)) {
        toast.error("Error check failed");
      }
    } finally {
      setIsCheckingErrors(false);
    }
  };

  // Note: Global hotkeys (Ctrl+Shift+Space) only work in the Desktop App (Electron)
  // The browser cannot capture hotkeys when minimized - this is a browser security limitation
  // Users should download the Desktop App for full hotkey support

  const transformTone = async (toneId) => {
    if (toneId === 'original') {
      if (originalTranscription) {
        setTranscription(originalTranscription);
        setCurrentTone(null);
        // Restored - no toast
      }
      setShowToneOptions(false);
      return;
    }

    if (!transcription) {
      // No text - silently return
      return;
    }

    try {
      setIsTransformingTone(true);
      setShowToneOptions(false);
      
      // Show special message for SEO-Webpage (takes longer)
      if (toneId === 'seo_webpage') {
        toast.info("Generating complete SEO specification with HTML... This may take 2-3 minutes.", { duration: 10000 });
      }
      if (toneId === 'website_build') {
        toast.info("Building complete website with all pages... This may take 3-5 minutes.", { duration: 15000 });
      }
      
      const isReroll = currentTone === toneId;
      
      // SEO-Webpage and Website Build generate massive output, need longer timeout
      const timeoutMs = (toneId === 'seo_webpage' || toneId === 'website_build') ? 300000 : 60000; // 5 min for SEO/Website, 1 min for others
      
      // Use current transcription (which may be translated) not original
      const response = await axios.post(`${API}/transform-tone`, {
        text: transcription,
        tone: toneId,
        reroll: isReroll
      }, { 
        withCredentials: true,
        timeout: timeoutMs
      });
      
      setTranscription(response.data.transformed_text);
      setCurrentTone(toneId);
      // Tone changed - visible in text
    } catch (error) {
      if (!handleSubscriptionError(error)) {
        toast.error("Tone transformation failed");
      }
    } finally {
      setIsTransformingTone(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const selectedLangDisplay = languages.find(l => l.code === selectedLanguage);

  const closeAllDropdowns = () => {
    setShowDownloadOptions(false);
    setShowToneOptions(false);
    setShowLanguageOptions(false);
  };

  // Check if running as installed PWA
  const isStandalonePWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  
  // Track if we've already set the initial window size
  const hasSetWindowSize = useRef(false);

  // Set fixed window size for PWA ONLY ONCE on initial load
  useEffect(() => {
    if (isStandalonePWA && !hasSetWindowSize.current) {
      hasSetWindowSize.current = true;
      
      // Fixed compact size - perfectly proportioned
      const fixedWidth = 336;
      const fixedHeight = 550;
      
      try {
        window.resizeTo(fixedWidth, fixedHeight);
        // Center the window on screen
        const left = Math.floor((window.screen.availWidth - fixedWidth) / 2);
        const top = Math.floor((window.screen.availHeight - fixedHeight) / 2);
        window.moveTo(left, top);
      } catch (e) {
        console.log('Could not set PWA window size:', e);
      }
    }
  }, []); // Empty dependency array - only run on mount

  return (
    <div className="h-screen w-full overflow-x-hidden flex flex-col relative" style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}>
      <audio ref={audioRef} />
      
      <HistorySidebar isOpen={showHistory} onClose={() => setShowHistory(false)} history={history} onLoad={loadFromHistory} onDelete={deleteFromHistory} />
      <ShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} shortcuts={shortcuts} onShortcutsChange={fetchShortcuts} />
      <SupportModal isOpen={showSupport} onClose={() => setShowSupport(false)} />
      <EmojiModal isOpen={showEmojiModal} onClose={() => setShowEmojiModal(false)} />
      
      {/* Subscription Modals */}
      <SubscribeModal 
        isOpen={showSubscribeModal} 
        onClose={() => { setShowSubscribeModal(false); setBlockReason(null); }} 
        onSubscribed={(plan) => { fetchSubscriptionStatus(); }}
        blockReason={blockReason}
      />
      <CancelSubscriptionModal 
        isOpen={showCancelModal} 
        onClose={() => setShowCancelModal(false)} 
        onCanceled={() => { fetchSubscriptionStatus(); }}
        currentPlan={subscriptionStatus?.plan}
      />

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70" onClick={() => setShowHelp(false)} />
          <div className="relative z-10 w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl p-6" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
            <button onClick={() => setShowHelp(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#f5f5f5' }}>📖 How to Use Voice Capture</h2>
            
            <div className="space-y-4 text-sm" style={{ color: '#a0aec0' }}>
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#0f1419' }}>
                <h3 className="font-semibold mb-1" style={{ color: '#22d3ee' }}>🎤 Recording</h3>
                <p>Press the microphone button to start recording. Speak naturally - there's no timeout, so you can pause to gather your thoughts. Press again to stop and transcribe.</p>
              </div>
              
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#0f1419' }}>
                <h3 className="font-semibold mb-1" style={{ color: '#f59e0b' }}>✓ Fix Errors</h3>
                <p>Automatically corrects grammar, spelling, and punctuation. Converts spoken numbers to proper format (e.g., "two hundred dollars" → "$200"). Also fixes garbled or unclear text to make it coherent and readable.</p>
              </div>
              
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#0f1419' }}>
                <h3 className="font-semibold mb-1" style={{ color: '#8b5cf6' }}>✨ Tone</h3>
                <p>Transform your text into different styles: Professional, Conversational, Direct, Empathetic, and many more. Click the same tone again for a different variation (re-roll).</p>
              </div>
              
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#0f1419' }}>
                <h3 className="font-semibold mb-1" style={{ color: '#3b82f6' }}>🌐 Translate</h3>
                <p>Translate your text into 50+ languages. Select a language from the dropdown and press GO.</p>
              </div>
              
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#0f1419' }}>
                <h3 className="font-semibold mb-1" style={{ color: '#10b981' }}>🔊 Read Aloud</h3>
                <p>Have your text read back to you with natural-sounding AI voice. Great for proofreading or accessibility.</p>
              </div>
              
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#0f1419' }}>
                <h3 className="font-semibold mb-1" style={{ color: '#22c55e' }}>⚡ Shortcuts</h3>
                <p>Save frequently used text snippets. Say "shortcut [name]" while recording to automatically insert saved text. Perfect for signatures, addresses, or common phrases.</p>
              </div>
              
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#0f1419' }}>
                <h3 className="font-semibold mb-1" style={{ color: '#ec4899' }}>🗣️ Voice Commands</h3>
                <p>Control the app hands-free with these voice commands:</p>
                <ul className="mt-2 space-y-1 text-xs">
                  <li><strong>"Start Transcription"</strong> - Start recording</li>
                  <li><strong>"End Transcription"</strong> - Stop & transcribe</li>
                  <li><strong>"Paste Transcription"</strong> - Paste the text</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hamburger Menu */}
      {showHamburgerMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowHamburgerMenu(false)} />
          <div className="fixed top-12 left-0 z-50 w-64 rounded-r-xl shadow-2xl" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
            <div className="p-2">
              {/* Tools Section */}
              <div className="px-3 py-1 text-xs font-medium uppercase" style={{ color: '#718096' }}>Tools</div>
              <button onClick={() => { setShowShortcuts(true); setShowHamburgerMenu(false); }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-3" style={{ color: '#f5f5f5' }}>
                <span>⚡</span> Shortcuts
              </button>
              <button onClick={() => { setShowEmojiModal(true); setShowHamburgerMenu(false); }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-3" style={{ color: '#f5f5f5' }}>
                <span>😊</span> Emoji Shortcuts
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#f59e0b20', color: '#f59e0b' }}>NEW</span>
              </button>
              <button onClick={() => { setShowHistory(true); setShowHamburgerMenu(false); }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-3" style={{ color: '#f5f5f5' }}>
                <span>📜</span> History
              </button>
              
              <div className="my-2" style={{ borderTop: '1px solid #2d3748' }} />
              
              {/* Help Section */}
              <div className="px-3 py-1 text-xs font-medium uppercase" style={{ color: '#718096' }}>Help</div>
              <button onClick={() => { setShowHelp(true); setShowHamburgerMenu(false); }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-3" style={{ color: '#f5f5f5' }}>
                <span>❓</span> Help
              </button>
              <button onClick={() => { setShowSupport(true); setShowHamburgerMenu(false); }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-3" style={{ color: '#f5f5f5' }}>
                <span>💬</span> Support
              </button>
              
              {/* Show subscription options */}
              {subscriptionStatus && subscriptionStatus.plan !== 'free_trial' && subscriptionStatus.status === 'active' && (
                <>
                  <div className="my-2" style={{ borderTop: '1px solid #2d3748' }} />
                  <button onClick={() => { setShowCancelModal(true); setShowHamburgerMenu(false); }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-3" style={{ color: '#f59e0b' }}>
                    <CreditCard className="h-4 w-4" /> Cancel Subscription
                  </button>
                </>
              )}
              {subscriptionStatus && subscriptionStatus.plan === 'free_trial' && (
                <>
                  <div className="my-2" style={{ borderTop: '1px solid #2d3748' }} />
                  <button onClick={() => { setShowSubscribeModal(true); setShowHamburgerMenu(false); }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-3" style={{ color: '#22c55e' }}>
                    <CreditCard className="h-4 w-4" /> Upgrade Plan
                  </button>
                </>
              )}
              <div className="my-2" style={{ borderTop: '1px solid #2d3748' }} />
              
              {/* Install App Section */}
              <div className="px-4 py-2">
                <p className="text-xs font-semibold mb-3" style={{ color: '#f5f5f5' }}>Install Options</p>
                
                {/* PWA Install - Available Now */}
                {installPrompt && (
                  <div className="mb-3">
                    <button 
                      onClick={() => { handleInstallClick(); setShowHamburgerMenu(false); }} 
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all hover:opacity-90"
                      style={{ backgroundColor: '#22c55e', color: '#fff' }}
                    >
                      <span>📲</span>
                      <span className="text-sm font-medium">Install App</span>
                    </button>
                  </div>
                )}
                
                {/* Windows Desktop - Download Installer */}
                <div className="mb-3">
                  <a 
                    href="/VoiceCapture-Setup.exe"
                    download="VoiceCapture-Setup.exe"
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all hover:opacity-90"
                    style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/></svg>
                    <span className="text-sm font-medium">Download for Windows</span>
                  </a>
                </div>
              </div>
              
              <div className="my-2" style={{ borderTop: '1px solid #2d3748' }} />
              <button onClick={onLogout} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-3" style={{ color: '#ef4444' }}>
                <LogOut className="h-4 w-4" /> Sign Out
              </button>
            </div>
          </div>
        </>
      )}

      {/* Header - Draggable title bar for Windows PWA */}
      <header 
        className="h-12 flex items-center justify-between px-3" 
        style={{ 
          backgroundColor: '#1a2332', 
          borderBottom: '1px solid #2d3748',
          WebkitAppRegion: isStandalonePWA ? 'drag' : 'no-drag' // Makes header draggable in PWA
        }}
      >
        <div className="flex items-center gap-1" style={{ WebkitAppRegion: 'no-drag' }}>
          <Button variant="ghost" size="sm" onClick={() => setShowHamburgerMenu(!showHamburgerMenu)} className="p-2 h-9 w-9" style={{ color: '#a0aec0' }}>
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        {/* Center area is draggable - shows recording indicator when active */}
        <div className="flex-1 flex items-center justify-center">
          {isRecording && (
            <div className="flex items-center gap-1.5" style={{ WebkitAppRegion: 'no-drag' }}>
              <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: '#ef4444' }} />
              <span className="text-sm font-mono" style={{ color: '#ef4444' }}>{formatTime(recordingTime)}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1" style={{ WebkitAppRegion: 'no-drag' }}>
          <Button 
            size="sm" 
            onClick={() => requireText(isSpeaking ? stopSpeaking : readAloud, 'Read Aloud')} 
            disabled={isPreparingSpeech}
            className="rounded-full px-3 h-8 text-xs hover:opacity-90" 
            style={{ backgroundColor: isSpeaking ? '#ef4444' : isPreparingSpeech ? '#6b7280' : '#10b981', color: '#fff' }}
          >
            {isPreparingSpeech ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Preparing...</>
            ) : isSpeaking ? (
              <><Square className="h-3.5 w-3.5 mr-1.5 fill-current" />Stop</>
            ) : (
              <><Volume2 className="h-3.5 w-3.5 mr-1.5" />Read Aloud</>
            )}
          </Button>
          
          {/* Window Controls - Desktop only (Android uses system gestures) */}
          {isStandalonePWA && !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
            <div className="flex items-center ml-2 gap-0.5">
              <button 
                onClick={() => window.blur()} 
                className="p-1.5 rounded hover:bg-gray-600" 
                title="Minimize"
                style={{ color: '#a0aec0' }}
              >
                <Minus className="h-4 w-4" />
              </button>
              <button 
                onClick={() => window.close()} 
                className="p-1.5 rounded hover:bg-red-600" 
                title="Close"
                style={{ color: '#a0aec0' }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Scrollable, optimized for floating windows */}
      <div className="flex-1 flex flex-col items-center justify-start p-2 sm:p-4 overflow-y-auto overflow-x-hidden">
        <div className="w-full max-w-xl flex flex-col gap-2 sm:gap-3 items-center min-h-min">
          
          {/* Logo + Voice Capture Title - Compact for floating windows */}
          <div className="flex flex-col items-center gap-0.5 sm:gap-1">
            <VoiceLogo />
            <h1 className="text-lg sm:text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Voice Capture</h1>
          </div>

          {/* Visualizer - Compact on small screens */}
          <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
            <VoiceVisualizer isActive={isRecording} />
            <div className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
              <span style={{ color: '#a0aec0' }}>Detected:</span>
              <span className="font-bold" style={{ color: '#10b981' }}>{detectedLanguage}</span>
            </div>
          </div>

          {/* Transcription Area - Smaller on floating windows */}
          <div className="w-full rounded-xl overflow-hidden relative" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
            <textarea
              ref={textareaRef}
              value={transcription}
              onChange={(e) => setTranscription(e.target.value)}
              placeholder="Press the microphone button. When done speaking, press the microphone button again and text will appear here."
              className="w-full h-24 sm:h-32 border-none focus:ring-0 focus:outline-none resize-none text-xs sm:text-sm leading-relaxed p-2 sm:p-3 overflow-y-auto"
              style={{ backgroundColor: 'transparent', color: '#f5f5f5', fontFamily: 'IBM Plex Sans, sans-serif' }}
            />
            {/* Tone hint at bottom right */}
            {currentTone && (
              <div className="absolute bottom-1 right-2 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded" style={{ backgroundColor: '#2d3748', color: '#a0aec0' }}>
                Click "{currentTone}" again for a new version
              </div>
            )}
          </div>

          {/* ROW 1: Copy, History, Shortcuts, Download, Clear */}
          <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-1.5">
            <Button size="sm" onClick={copyToClipboard} className="rounded-full px-2 sm:px-2.5 h-6 sm:h-7 text-[10px] sm:text-[11px] hover:opacity-90" style={{ backgroundColor: showCopied ? '#22c55e' : '#4b5563', color: '#fff', border: '1px solid #6b7280' }}>
              <Copy className="h-2.5 sm:h-3 w-2.5 sm:w-3 mr-0.5 sm:mr-1" />{showCopied ? 'Copied!' : 'Copy'}
            </Button>

            <Button size="sm" onClick={checkErrors} disabled={!transcription || isCheckingErrors} className="rounded-full px-2 sm:px-2.5 h-6 sm:h-7 text-[10px] sm:text-[11px] hover:opacity-90" style={{ backgroundColor: '#f59e0b', color: '#fff' }}>
              {isCheckingErrors ? <Loader2 className="h-2.5 sm:h-3 w-2.5 sm:w-3 animate-spin mr-0.5 sm:mr-1" /> : '✓ '}Fix Errors
            </Button>

            <Button size="sm" onClick={() => setShowHistory(true)} className="rounded-full px-2 sm:px-2.5 h-6 sm:h-7 text-[10px] sm:text-[11px] hover:opacity-90" style={{ backgroundColor: '#4b5563', color: '#fff', border: '1px solid #6b7280' }}>
              📜 History
            </Button>

            <Button size="sm" onClick={() => setShowShortcuts(true)} className="rounded-full px-2 sm:px-2.5 h-6 sm:h-7 text-[10px] sm:text-[11px] hover:opacity-90" style={{ backgroundColor: '#3b82f6', color: '#fff' }}>
              ⚡ Shortcuts
            </Button>

            {/* Download dropdown */}
            <div className="relative">
              <Button size="sm" onClick={() => { closeAllDropdowns(); setShowDownloadOptions(!showDownloadOptions); }} className="rounded-full px-2 sm:px-2.5 h-6 sm:h-7 text-[10px] sm:text-[11px] hover:opacity-90" style={{ backgroundColor: '#4b5563', color: '#fff', border: '1px solid #6b7280' }}>
                <Download className="h-2.5 sm:h-3 w-2.5 sm:w-3 mr-0.5 sm:mr-1" />Download
              </Button>
              {showDownloadOptions && (
                <div className="absolute top-full mt-1 left-1/2 transform -translate-x-1/2 rounded-lg shadow-xl p-1.5 z-30 min-w-[70px]" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
                  {['txt', 'csv', 'pdf'].map(fmt => (
                    <button key={fmt} onClick={() => downloadTranscription(fmt)} className="block w-full text-left px-2.5 py-1.5 text-xs rounded hover:bg-gray-700" style={{ color: '#f5f5f5' }}>
                      {fmt.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button size="sm" onClick={clearTranscription} className="rounded-full px-2 sm:px-2.5 h-6 sm:h-7 text-[10px] sm:text-[11px] hover:opacity-90" style={{ backgroundColor: '#4b5563', color: '#fff', border: '1px solid #6b7280' }}>
              Clear
            </Button>
          </div>

          {/* ROW 2: Tone, Translate, Language, GO */}
          <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-1.5">
            {/* Tone dropdown */}
            <div className="relative">
              <Button size="sm" onClick={() => { if (!transcription) { return; } closeAllDropdowns(); setShowToneOptions(!showToneOptions); }} className="rounded-full px-2 sm:px-2.5 h-6 sm:h-7 text-[10px] sm:text-[11px] hover:opacity-90" style={{ backgroundColor: '#8b5cf6', color: '#fff' }}>
                {isTransformingTone ? <Loader2 className="h-2.5 sm:h-3 w-2.5 sm:w-3 animate-spin" /> : <><Sparkles className="h-2.5 sm:h-3 w-2.5 sm:w-3 mr-0.5 sm:mr-1" />Tone</>}
              </Button>
              {showToneOptions && (
                <div className="absolute top-full mt-1 left-1/2 transform -translate-x-1/2 rounded-lg shadow-xl p-1.5 z-30 min-w-[140px] max-h-[180px] overflow-y-auto" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
                  {toneOptions.map((tone) => (
                    <button 
                      key={tone.id} 
                      onClick={() => transformTone(tone.id)} 
                      className="w-full text-left px-2.5 py-1.5 text-xs rounded hover:bg-gray-700 flex items-center gap-1.5" 
                      style={{ color: tone.id === 'original' ? '#10b981' : '#f5f5f5' }}
                    >
                      <span>{tone.icon}</span> {tone.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button size="sm" onClick={() => requireText(translateText, 'Translate')} className="rounded-full px-2 sm:px-2.5 h-6 sm:h-7 text-[10px] sm:text-[11px] hover:opacity-90" style={{ backgroundColor: '#6366f1', color: '#fff' }}>
              {isTranslating ? <Loader2 className="h-2.5 sm:h-3 w-2.5 sm:w-3 animate-spin" /> : <><Languages className="h-2.5 sm:h-3 w-2.5 sm:w-3 mr-0.5 sm:mr-1" />Translate</>}
            </Button>

            {/* Language Dropdown */}
            <div className="relative">
              <button onClick={() => { closeAllDropdowns(); setShowLanguageOptions(!showLanguageOptions); }} className="flex items-center gap-1 rounded-full px-2 py-1 h-7 text-[11px] border-none cursor-pointer hover:opacity-80" style={{ backgroundColor: '#4b5563', color: '#fff' }}>
                <span style={{ fontSize: '12px' }}>{selectedLangDisplay?.flag}</span>
                <span>{selectedLangDisplay?.name}</span>
                <span style={{ fontSize: '8px' }}>▼</span>
              </button>
              {showLanguageOptions && (
                <div className="absolute top-full mt-1 left-1/2 transform -translate-x-1/2 rounded-lg shadow-xl p-2 z-30 w-[260px] max-h-[200px] overflow-y-auto" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
                  <div className="grid grid-cols-2 gap-1">
                    {languages.map((lang) => (
                      <button key={lang.code} onClick={() => { setSelectedLanguage(lang.code); setShowLanguageOptions(false); }} className={`text-left px-2 py-1 text-xs rounded hover:bg-gray-700 flex items-center gap-1 ${selectedLanguage === lang.code ? 'bg-gray-700' : ''}`} style={{ color: '#f5f5f5' }}>
                        <span style={{ fontSize: '12px' }}>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button size="sm" onClick={() => requireText(translateText, 'Translate')} className="rounded-full px-2 sm:px-3 h-6 sm:h-7 text-[10px] sm:text-[11px] font-bold hover:opacity-90" style={{ backgroundColor: '#10b981', color: '#fff' }}>
              {isTranslating ? <Loader2 className="h-2.5 sm:h-3 w-2.5 sm:w-3 animate-spin" /> : "GO"}
            </Button>
          </div>

          {/* Record Button - Responsive size for floating windows */}
          <div className="flex flex-col items-center gap-1.5 sm:gap-2 mt-2 sm:mt-3">
            <Button 
              onClick={toggleRecording} 
              disabled={isProcessing} 
              className={`rounded-full h-16 w-16 sm:h-20 sm:w-20 p-0 shadow-lg transition-all hover:scale-105 ${isRecording ? 'animate-pulse' : ''}`} 
              style={{ 
                backgroundColor: isRecording ? '#ef4444' : '#3b82f6', 
                color: '#fff', 
                boxShadow: isRecording ? '0 0 30px rgba(239, 68, 68, 0.7)' : '0 0 20px rgba(59, 130, 246, 0.5)' 
              }}
            >
              {isProcessing ? <Loader2 className="h-6 sm:h-8 w-6 sm:w-8 animate-spin" /> : isRecording ? <Square className="h-5 sm:h-7 w-5 sm:w-7 fill-current" /> : <Mic className="h-6 sm:h-8 w-6 sm:w-8" />}
            </Button>
            <p className="text-xs sm:text-sm" style={{ color: '#a0aec0' }}>
              {isProcessing ? "Processing & auto-fixing..." : isRecording ? "Recording... Click or press again to transcribe" : "Click or press to record"}
            </p>
            {/* Keyboard shortcuts hint - desktop only */}
            <div className="hidden sm:flex flex-col items-center gap-1 text-[10px] mt-1" style={{ color: '#718096' }}>
              <span className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: '#3b82f6', color: '#fff' }}>Ctrl+Win</kbd>
                  <span>Record</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: '#22c55e', color: '#fff' }}>Ctrl+V</kbd>
                  <span>Paste</span>
                </span>
              </span>
              <span style={{ color: '#22c55e' }}>✨ Works when minimized (Desktop App)</span>
            </div>
            {/* Voice commands toggle button */}
            <button
              onClick={() => {
                if (voiceCommandsEnabled) {
                  setVoiceCommandsEnabled(false);
                  toast.info("Voice commands disabled", { duration: 2000 });
                } else {
                  setVoiceCommandsEnabled(true);
                  toast.success("Voice commands ON! Say 'Start Transcription'", { duration: 3000 });
                }
              }}
              className="flex items-center gap-2 mt-2 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all hover:opacity-80"
              style={{ 
                backgroundColor: voiceCommandsListening ? '#ec489920' : voiceCommandsEnabled ? '#ec489910' : '#1a2332',
                color: voiceCommandsListening ? '#ec4899' : voiceCommandsEnabled ? '#ec4899' : '#718096',
                border: `1px solid ${voiceCommandsListening ? '#ec4899' : voiceCommandsEnabled ? '#ec489950' : '#2d3748'}`
              }}
            >
              {voiceCommandsListening ? (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500"></span>
                  </span>
                  <span>🗣️ Listening for voice commands...</span>
                </>
              ) : voiceCommandsEnabled ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-pink-500"></span>
                  <span>🗣️ Voice Commands ON - Click to disable</span>
                </>
              ) : (
                <>
                  <span className="h-2 w-2 rounded-full bg-gray-500"></span>
                  <span>🗣️ Click to enable Voice Commands</span>
                </>
              )}
            </button>
            {voiceCommandError && (
              <p className="text-[10px] mt-1" style={{ color: '#ef4444' }}>{voiceCommandError}</p>
            )}
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showDownloadOptions || showToneOptions || showLanguageOptions) && (
        <div className="fixed inset-0 z-20" onClick={closeAllDropdowns} />
      )}
    </div>
  );
}
